import { StormBuildUpRowDef } from './stormBuildUpTypes';
import { applySectionTitles } from './stormBuildUpTitles';

// Per-tank configurable field, same registry shape as stormBuildUpConcAttenuation.ts's
// CONC_CONFIGURABLE_FIELDS — FP/OHP are percentages (decimals allowed),
// stored in a generic Record<string, number> map.
export interface WeholiteConfigurableField {
  suffix: string; // row id suffix, e.g. `wh_2_${suffix}`
  label: string;
  describe: (pct: number) => string;
}

export const WEHOLITE_CONFIGURABLE_FIELDS: WeholiteConfigurableField[] = [
  { suffix: 'fixedPrice', label: 'FP (%)', describe: (pct) => `Add ${pct}% FP on above` },
  { suffix: 'ohp', label: 'OHP (%)', describe: (pct) => `Add ${pct}% OHP on above` },
];

// No default value: matches stormBuildUpConcAttenuation.ts's convention — an
// unconfigured row keeps showing its static "xxx" placeholder text, only
// switching to the real value once that tank's Configure modal sets one > 0.
const WEHOLITE_FP_PLACEHOLDER = 'Add xxx% FP on above';
const WEHOLITE_OHP_PLACEHOLDER = 'Add xxx% OHP on above';

// rowId is the full `wh_<n>_<suffix>` id — parses out the suffix and looks up
// its registry entry, if any (mirrors stormBuildUpConcAttenuation.ts's getConcConfigurableField).
export const getWeholiteConfigurableField = (rowId: string): WeholiteConfigurableField | undefined => {
  const match = rowId.match(/^wh_\d+_(.+)$/);
  if (!match) return undefined;
  return WEHOLITE_CONFIGURABLE_FIELDS.find((f) => f.suffix === match[1]);
};

const makeWeholiteTankRows = (n: number, weholiteConfigPct: Record<string, number> = {}, supplierName: string = 'Weholite'): StormBuildUpRowDef[] => {
  const fpPct = weholiteConfigPct[`wh_${n}_fixedPrice`];
  const ohpPct = weholiteConfigPct[`wh_${n}_ohp`];
  return [
  { id: `wh_${n}_hdr`,          description: `${supplierName} Attenuation Tank ${n}`,               unit: '',     defaultQuant: null, isHeader: true },
  { id: `wh_${n}_excPit`,       description: 'Excavate pit (all-in)',                               unit: 'm3',   defaultQuant: null },
  { id: `wh_${n}_levelComp`,    description: 'Level and compact',                                   unit: 'm2',   defaultQuant: null },
  { id: `wh_${n}_granBase`,     description: 'Granular base',                                       unit: 'm3',   defaultQuant: null },
  { id: `wh_${n}_supplyTank`,   description: `Supply of ${supplierName} Tank`,                      unit: 'item', defaultQuant: null },
  { id: `wh_${n}_welding`,      description: `${supplierName} welding`,                             unit: 'item', defaultQuant: null },
  { id: `wh_${n}_carriage`,     description: `${supplierName} carriage`,                            unit: 'item', defaultQuant: null },
  { id: `wh_${n}_fixedPrice`,   description: fpPct ? WEHOLITE_CONFIGURABLE_FIELDS[0].describe(fpPct) : WEHOLITE_FP_PLACEHOLDER, unit: '%', defaultQuant: null },
  { id: `wh_${n}_ohp`,          description: ohpPct ? WEHOLITE_CONFIGURABLE_FIELDS[1].describe(ohpPct) : WEHOLITE_OHP_PLACEHOLDER, unit: '%', defaultQuant: null },
  { id: `wh_${n}_offload21T`,   description: 'Offloading - 21T operated excavator',                 unit: 'day',  defaultQuant: null },
  { id: `wh_${n}_offload2men`,  description: 'Offloading - 2 men',                                 unit: 'day',  defaultQuant: null },
  { id: `wh_${n}_lpHdr`,        description: 'L&P for Tank Installation & Turrets',                 unit: '',     defaultQuant: null, isSubHeader: true },
  { id: `wh_${n}_install1`,     description: 'M&J R+S gang - Install',                             unit: 'days', defaultQuant: null },
  { id: `wh_${n}_install2`,     description: 'M&J R+S gang - Install (2)',                         unit: 'days', defaultQuant: null },
  { id: `wh_${n}_totalBed`,     description: 'Total Bed & Surround',                                unit: 'm3',   defaultQuant: null },
  { id: `wh_${n}_asDug`,        description: 'As dug backfill to void',                             unit: 'm3',   defaultQuant: null },
  { id: `wh_${n}_stockpile`,    description: 'Stockpile surplus onsite',                            unit: 'm3',   defaultQuant: null },
  { id: `wh_${n}_foundCover`,   description: 'Foundation for cover slab',                           unit: 'nr',   defaultQuant: null },
  { id: `wh_${n}_coverSlab`,    description: '1200mmØ cover slab',                                  unit: 'nr',   defaultQuant: null },
  { id: `wh_${n}_seatingRings`, description: 'Seating Rings',                                       unit: 'nr',   defaultQuant: null },
  { id: `wh_${n}_cf`,           description: 'D400 Cover & Frame',                                  unit: 'nr',   defaultQuant: null },
  ];
};

export const DEFAULT_WEHOLITE_SECTIONS = 1;
export const MAX_WEHOLITE_SECTIONS = 15;

// Data-row (non-header) id suffixes for one tank — used by StormSewersTakeoff.tsx's
// handleAddWeholiteTank to seed a freshly-added tank's rows at qty 0, same
// convention as stormBuildUpCellularAttenuation.ts's CELLULAR_ROW_SUFFIXES.
export const WEHOLITE_ROW_SUFFIXES = [
  'excPit', 'levelComp', 'granBase', 'supplyTank', 'welding', 'carriage',
  'fixedPrice', 'ohp', 'offload21T', 'offload2men',
  'install1', 'install2', 'totalBed', 'asDug', 'stockpile', 'foundCover', 'coverSlab',
  'seatingRings', 'cf',
];

export const getWeholiteAttenuationRows = (
  sectionCount: number,
  weholiteConfigPct: Record<string, number> = {},
  supplierName?: string,
  sectionTitles: Record<string, string> = {}
): StormBuildUpRowDef[] =>
  applySectionTitles(Array.from({ length: sectionCount }, (_, i) => makeWeholiteTankRows(i + 1, weholiteConfigPct, supplierName)).flat(), sectionTitles);

// Full fixed set — kept for parity with the other stormBuildUp* modules.
export const WEHOLITE_ATTENUATION_ROWS: StormBuildUpRowDef[] = getWeholiteAttenuationRows(MAX_WEHOLITE_SECTIONS);

// Shifts a row-id-keyed map's `wh_<n>_*` keys down by one wherever n >
// deletedN, and drops the deleted section's own keys — mirrors
// shiftCellularAttenuationSectionKeys/shiftPondSectionKeys.
export const shiftWeholiteSectionKeys = <T,>(map: Record<string, T>, deletedN: number): Record<string, T> => {
  const next: Record<string, T> = {};
  Object.entries(map).forEach(([id, value]) => {
    const match = id.match(/^wh_(\d+)_(.+)$/);
    if (!match) {
      next[id] = value;
      return;
    }
    const idx = Number(match[1]);
    if (idx === deletedN) return;
    const newIdx = idx > deletedN ? idx - 1 : idx;
    next[`wh_${newIdx}_${match[2]}`] = value;
  });
  return next;
};
